<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="1024"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          extension-height="40"
          flat
          height="80"
        >
          <v-avatar :class="$t('Restaurant.MenuHistoryDialog.Toolbar.Color')">
            <v-icon dark>
              {{ $t('Restaurant.MenuHistoryDialog.Toolbar.Icon') }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              <v-chip
                class="mb-1 px-1 font-weight-bold"
                label
                small
              >
                #{{ menu?.Id }}
              </v-chip>

              {{ getProductLangField('Name') }}
            </div>

            <div class="text-caption">
              {{ $t('Restaurant.MenuHistoryDialog.Toolbar.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="px-1 py-0">
          <v-data-table
            :footer-props="dataTable.footerProps"
            :headers="dataTableHeaders"
            :height="dataTable.resize.bodyHeight"
            :items="dataTable.data"
            :loading="dataTable.loading"
            :loading-text="dataTable.text.loadingText"
            :no-data-text="dataTable.text.noDataText"
            :no-results-text="dataTable.text.noResultsText"
            :options.sync="dataTable.options"
            :search="dataTable.search.terms"
            :server-items-length="dataTable.total"
            class="menu-history-table"
            fixed-header
            item-key="Id"
          >
            <template #[`item.id`]="{item}">
              {{ item.Id }}
            </template>

            <template #[`item.menu_id`]="{item}">
              {{ item.MenuId }}
            </template>

            <template #[`item.description`]="{item}">
              {{ item.Description }}
            </template>

            <template #[`item.user`]="{item}">
              <div v-if="item.User">
                {{ item.User }}
              </div>

              <div
                v-if="item.Username"
                class="grey--text"
              >
                {{ item.Username }}
              </div>
            </template>

            <template #[`item.created_at`]="{item}">
              {{ item.CreatedAt }}
            </template>

            <template #[`item.actions`]="{item}">
              <v-btn
                :class="$t('Log.Table.Button.View.Color')"
                :small="$vuetify.breakpoint.xsOnly"
                class="ma-0 mr-1"
                dark
                icon
                @click="showItemDetails(item)"
              >
                <v-icon :small="$vuetify.breakpoint.xsOnly">
                  {{ $t('Log.Table.Button.View.Icon') }}
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <activity-log-dialog
      :item="selectedItem"
      :menu-name="getProductLangField('Name')"
      :visible.sync="detailsDialogVisible"
    />
  </div>
</template>

<script>
import AppData from '@/mixins/appdata.js'
import Auth from '@/mixins/auth.js'
import DataTable from '@/mixins/data-table/dataTable.js'
import ActivityLogDialog from '@/components/activity-log/ActivityLogDialog.vue'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import FilterCommon from '@/mixins/filter/filterCommon.js'

export default {
  name      : 'MenuHistoryDialog',
  components: {FilterOptions, ActivityLogDialog},
  mixins    : [Auth, AppData, DataTable, FilterCommon],
  props     : {
    visible: {
      type   : Boolean,
      default: false
    },
    menu: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      selectedItem        : null,
      detailsDialogVisible: false,
      dataTable           : {
        autoload     : false,
        socketCommand: {
          list: window.SocketCommand.Menu.GetHistoryAdmin
        },
        payload: {
          menu_id: null
        },
        options: {
          page         : 1,
          itemsPerPage : 10,
          defaultSortBy: 'id',
          sortBy       : ['id'],
          sortDesc     : [true]
        },
        resize: {
          enabled   : true,
          offset    : 200,
          bodyHeight: 0
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : this.$t('Restaurant.MenuHistoryDialog.Table.Headers.Id'),
            value    : 'id',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.MenuHistoryDialog.Table.Headers.Product'),
            value    : 'menu_id',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.MenuHistoryDialog.Table.Headers.Action'),
            value    : 'description',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.MenuHistoryDialog.Table.Headers.User'),
            value    : 'user',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.MenuHistoryDialog.Table.Headers.Date'),
            value    : 'created_at',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : '',
            value    : 'actions',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          }
        ]
      },
      filtersModelFieldName: 'filtersModelDialog',
      filtersModelDialog   : {
        lang      : 'Restaurant.MenuHistoryDialog',
        filterDate: {
          key    : 'filter_date',
          value  : null,
          visible: false,
          date   : null
        }
      }
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    isVisible(newVal) {
      this.initData()
    }
  },
  created() {
    // console.log('1. created');
  },
  mounted() {
    // console.log('2. mounted');
    this.$bus.$on(window.SocketCommand.Customer.Get + '-voip', this.setCustomer)
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.Customer.Get + '-voip', this.setCustomer)
  },
  methods: {
    initData() {
      this.selectedItem = null
      this.detailsDialogVisible = false
      this.filtersModelDialog.filterDate.value = null
      this.filtersModelDialog.filterDate.date = null
      this.detailsDialogVisible = false
      this.dataTable.payload.id = null
      this.dataTable.options.page = 1
      this.dataTable.total = 0
      this.dataTable.data = []
      this.dataTable.loaded = false

      if (this.menu && this.isVisible) {
        this.dataTable.payload.menu_id = this.menu.Id
        this.getData()
      }
    },

    showItemDetails(item) {
      this.selectedItem = item
      this.detailsDialogVisible = true
    },

    onCancelButtonClick() {
      this.isVisible = false
    },

    getProductLangField(fieldName) {
      const lang = this.menu?.Lang[this.$i18n.locale]
      return (lang && lang[fieldName]) || this.menu?.[fieldName] || ''
    }

  }
}
</script>

<style scoped>

</style>
